import type { HeroMood } from '@kidshome/hero-content/art';
import type { EffortRatesResponse } from '@kidshome/api-contract';

/**
 * Effort → ✨/bond. The award table (chore/habit/learning weights, ADR-0095 §2.1 · ADR-0101 §2) is
 * SERVER-OWNED and served on every `GET /v1/play/hero` envelope as `effortRates` — surfaces render the
 * SERVED amounts via `useHero().effortRates`, never a local mirror that could silently drift from the
 * backend. A "+N" reaction must reflect the served table — a true consequence of a completed action —
 * and must only ever appear at the moment that action succeeds, never as a decorative constant.
 */
export type EffortRates = EffortRatesResponse;

export type EffortKind = keyof EffortRates;

/**
 * The offline / first-paint FALLBACK for the served table — used ONLY while the hero read is unresolved
 * (the offline PWA must still label a just-completed effort). Mirrors the backend defaults and is
 * superseded by the wire value the moment `GET /v1/play/hero` lands.
 */
export const EFFORT_RATES_FALLBACK: EffortRates = {
  chore: 10,
  habit: 6,
  learning: 8,
};

/**
 * The effort KIND a PAST narrator beat recorded, inferred from its closed VoiceKey — used only to label
 * the historical "recent moments" feed (a record of what already happened), never to drive a live
 * reaction. The amount is then read off the served `effortRates[kind]`.
 */
export function effortKindForVoiceKey(key: string): EffortKind | null {
  if (key.includes('chore')) return 'chore';
  if (key.includes('habit')) return 'habit';
  if (key.includes('learn')) return 'learning';
  return null;
}

/**
 * Map a narrator beat's VoiceKey to the hero's calm MOOD (ADR-0102). A beat that records real effort or
 * growth (a chore/habit/learning win, a level/evolution) makes the hero CHEER; everything else stays IDLE.
 * The single client-side source for "should the hero react?" — the figure expresses the SAME positive
 * event the `+N` pill labels, instead of a decorative constant.
 */
export function moodForReaction(key: string): HeroMood {
  if (
    effortKindForVoiceKey(key) !== null ||
    key.includes('level') ||
    key.includes('evolve') ||
    key.includes('grow')
  ) {
    return 'cheer';
  }
  return 'idle';
}
